import React from 'react';
import { Progress } from 'src/design/atoms/Progress';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Dropzone } from 'src/design/organisms/Dropzone';
import { DropOverlay } from 'src/design/organisms/DropOverlay';
import { DocumentDto } from 'src/api/FiretrolApiClient';
import { useDocumentUpload } from '../useDocumentUpload';
import { FolderHeader } from './FolderHeader';
import { FileTable } from './FileTable';
import classes from './DocumentBrowser.module.css';

export interface IDocumentBrowserProps {
  jobId: number;
  currentFolderId: string;
  currentFolder: DocumentDto | null;
  breadcrumbs: DocumentDto[];
  subfolders: DocumentDto[];
  documents: DocumentDto[];
  isSelectedLocked: boolean;
  canDeleteFolder: boolean;
  isLoading: boolean;
  selectedDocumentId: string | null;
  openFilePicker: React.MutableRefObject<(() => void) | null>;
  onNavigate: (folderId: string) => void;
  onSelectDocument: (document: DocumentDto) => void;
  onEditFolder: () => void;
  onDeleteFolder: () => Promise<void>;
  onOpenSidebar?: () => void;
}

export const DocumentBrowser: React.FC<IDocumentBrowserProps> = ({
  jobId,
  currentFolderId,
  currentFolder,
  breadcrumbs,
  subfolders,
  documents,
  isSelectedLocked,
  canDeleteFolder,
  isLoading,
  selectedDocumentId,
  openFilePicker,
  onNavigate,
  onSelectDocument,
  onEditFolder,
  onDeleteFolder,
  onOpenSidebar,
}) => {
  const [search, setSearch] = React.useState('');

  React.useEffect(() => setSearch(''), [currentFolderId]);

  const {
    uploadFiles,
    showProgress,
    targetCount,
    completeCount,
    uploadPercent,
  } = useDocumentUpload({
    jobId,
    folderId: currentFolderId,
  });

  return (
    <Dropzone
      unstyled
      enablePointerEvents
      activateOnClick={false}
      useFsAccessApi={false}
      openRef={openFilePicker}
      onDrop={uploadFiles}
      className={classes.pane}
    >
      <Dropzone.Accept>
        <DropOverlay
          title={`Drop to upload to ${currentFolder?.name ?? 'All documents'}`}
        />
      </Dropzone.Accept>

      {showProgress && (
        <Stack gap={2} className={classes.progress}>
          <Progress
            size={3}
            radius={0}
            value={uploadPercent * 100}
            animated={uploadPercent < 1}
          />
          <Text size="xs" c="dimmed" ta="right" pr="md">
            Uploading {completeCount} of {targetCount}
          </Text>
        </Stack>
      )}

      <FolderHeader
        breadcrumbs={breadcrumbs}
        currentFolder={currentFolder}
        isSelectedLocked={isSelectedLocked}
        canDeleteFolder={canDeleteFolder}
        search={search}
        onSearchChange={setSearch}
        onNavigate={onNavigate}
        onEditFolder={onEditFolder}
        onDeleteFolder={onDeleteFolder}
        onOpenSidebar={onOpenSidebar}
      />

      <div className={classes.body}>
        <FileTable
          folders={subfolders}
          documents={documents}
          search={search}
          isLoading={isLoading}
          selectedDocumentId={selectedDocumentId}
          onOpenFolder={onNavigate}
          onSelectDocument={onSelectDocument}
          onUploadClick={() => openFilePicker.current?.()}
        />
      </div>
    </Dropzone>
  );
};
